import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useEffect, useMemo } from "react";
import { useApp } from "@/store/app";
import { ALL_BADGES, localizeBadge } from "@/lib/badges";
import { BadgeIcon } from "@/components/BadgeIcon";
import { useT, useLang, useTCountry } from "@/i18n";
import { toast } from "sonner";
import { Shield, Check, RotateCcw, Trophy, Radio, Sparkles, RefreshCw, Save, Clock, Trash2, Users, Lock, Unlock, ListChecks, ChevronDown } from "lucide-react";
import { useTeams, type TeamRow } from "@/hooks/useTeams";
import { useMatches, useUpdateMatch, type MatchRow, type ScorerSide } from "@/hooks/useMatches";
import { usePlayersByTeam, type PlayerRow } from "@/hooks/usePlayersByTeam";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { AuditLogPanel } from "@/components/AuditLogPanel";
import { supabase } from "@/integrations/supabase/client";
import { MATCHES } from "@/data/matches";
import { useQueryClient } from "@tanstack/react-query";
import { errorMessage } from "@/lib/errorMessage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export const Route = createFileRoute("/_app/admin")({
  component: AdminPage,
});

const TIMEZONES = [
  "Europe/Bucharest",
  "UTC",
  "Europe/London",
  "Europe/Madrid",
  "Europe/Paris",
  "America/New_York",
  "America/Mexico_City",
  "America/Los_Angeles",
  "Asia/Tokyo",
];

type TriggerCheckRow = {
  trigger_name: string;
  table_name: string;
  purpose: string;
  present: boolean;
  enabled: string;
};

function AdminPage() {
  const t = useT();
  const lang = useLang((s) => s.lang);
  const previewBadges = useApp((s) => s.previewBadges);
  
  const qc = useQueryClient();

  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const { data: teams = [] } = useTeams();
  const { data: matches = [], isLoading } = useMatches();
  const { data: playersIdx } = usePlayersByTeam();

  // Resolve W/L slot placeholders from finished match results so the admin
  // panel shows real teams even when propagateWinner hasn't written to DB yet.
  const resolvedMatches = useMemo(() => {
    const winnerOf: Record<number, string | null> = {};
    for (const m of matches) {
      if (m.status !== "finished") continue;
      const h = m.home_score ?? 0, a = m.away_score ?? 0;
      const hp = m.home_pen_score, ap = m.away_pen_score;
      if (h > a) winnerOf[m.id] = m.home_team;
      else if (a > h) winnerOf[m.id] = m.away_team;
      else if (hp != null && ap != null && hp !== ap)
        winnerOf[m.id] = hp > ap ? m.home_team : m.away_team;
    }
    return matches.map((m) => {
      let home = m.home_team;
      let away = m.away_team;
      if (!home && m.home_slot_label) {
        const id = /^W(\d+)$/.exec(m.home_slot_label)?.[1];
        if (id) home = winnerOf[+id] ?? null;
      }
      if (!away && m.away_slot_label) {
        const id = /^W(\d+)$/.exec(m.away_slot_label)?.[1];
        if (id) away = winnerOf[+id] ?? null;
      }
      return home === m.home_team && away === m.away_team
        ? m
        : { ...m, home_team: home, away_team: away };
    });
  }, [matches]);

  const [resettingPreds, setResettingPreds] = useState(false);
  const [resettingLeagues, setResettingLeagues] = useState(false);
  const [recalcBadges, setRecalcBadges] = useState(false);
  const [verifyingTriggers, setVerifyingTriggers] = useState(false);
  const [checkingStatuses, setCheckingStatuses] = useState(false);
  const [triggerChecks, setTriggerChecks] = useState<TriggerCheckRow[] | null>(null);

  async function handleCheckMatchStatuses() {
    setCheckingStatuses(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("update_match_statuses");
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ["matches"] });
      const updated = typeof data === "number" ? data : 0;
      toast.success(
        t("Match statuses updated", undefined, "Statusuri actualizate"),
        { description: t(`${updated} match(es) flipped to live`, undefined, `${updated} meci(uri) trecut(e) la live`) }
      );
    } catch (err) {
      toast.error(
        t("Status check failed", undefined, "Verificarea statusurilor a eșuat"),
        { description: errorMessage(err) }
      );
    } finally {
      setCheckingStatuses(false);
    }
  }

  async function handleRecalculateBadges() {
    setRecalcBadges(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("recalculate_all_badges");
      if (error) throw error;
      await qc.invalidateQueries({ queryKey: ["user_badges"] });
      toast.success(
        t("Badges recalculated", "Insignele au fost recalculate"),
        { description: t(`${data ?? 0} users processed`, `${data ?? 0} utilizatori procesați`) }
      );
    } catch (err) {
      toast.error(t("Recalculation failed", "Recalculare eșuată"), { description: errorMessage(err) });
    } finally {
      setRecalcBadges(false);
    }
  }

  async function handleVerifyTriggers() {
    setVerifyingTriggers(true);
    try {
      const { data, error } = await (supabase as any).rpc("verify_admin_triggers");
      if (error) throw error;
      const rows = (data ?? []) as TriggerCheckRow[];
      setTriggerChecks(rows);
      const missing = rows.filter((row) => !row.present || row.enabled !== "enabled");
      if (missing.length) {
        toast.warning(t("Trigger check found issues", undefined, "Verificarea triggerelor a găsit probleme"), {
          description: missing.map((row) => row.trigger_name).join(", "),
        });
      } else {
        toast.success(t("All core triggers are active", undefined, "Toate trigger-ele principale sunt active"), {
          description: t("Scoring, notifications and badges are wired.", undefined, "Punctarea, notificările și insignele sunt conectate."),
        });
      }
    } catch (err) {
      toast.error(t("Trigger check failed", undefined, "Verificarea triggerelor a eșuat"), { description: errorMessage(err) });
    } finally {
      setVerifyingTriggers(false);
    }
  }

  const teamName = (code: string | null) =>
    code ? (teams.find((tt) => tt.code === code)?.name ?? code) : "";

  async function handleResetAllPredictions() {
    setResettingPreds(true);
    try {
      const kickoffs: Record<string, string> = {};
      for (const m of MATCHES) kickoffs[String(m.id)] = m.date;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any).rpc("admin_reset_predictions", {
        p_kickoffs: kickoffs,
      });
      if (error) throw error;
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["matches"] }),
        qc.invalidateQueries({ queryKey: ["predictions"] }),
      ]);
      toast.success(t("All predictions reset", "Toate predicțiile au fost resetate"));
    } catch (err) {
      toast.error(t("Reset failed", "Resetare eșuată"), { description: errorMessage(err) });
    } finally {
      setResettingPreds(false);
    }
  }

  async function handleDeleteAllLeagues() {
    setResettingLeagues(true);
    try {
      const { error } = await (supabase as unknown as {
        from: (t: string) => ReturnType<typeof supabase.from>;
      })
        .from("leagues")
        .delete()
        .eq("is_protected", false);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["my-leagues"] });
      toast.success(t("All leagues deleted", "Toate ligile au fost șterse"));
    } catch (err) {
      toast.error(t("Delete failed", "Ștergere eșuată"), { description: errorMessage(err) });
    } finally {
      setResettingLeagues(false);
    }
  }

  const finishedCount = resolvedMatches.filter((m) => m.status === "finished").length;

  if (adminLoading) {
    return <div className="py-12 text-center text-muted-foreground">{t("Loading…", "Carregando…")}</div>;
  }

  if (!isAdmin) {
    return (
      <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-center">
        <Shield className="mx-auto mb-2 h-6 w-6 text-destructive" />
        <h1 className="text-lg font-bold">{t("Admins only", "Somente administradores")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t(
            "Your account doesn't have the admin role. Ask an admin to grant it via the user_roles table.",
            "Sua conta não tem a função de administrador. Peça a um admin para conceder via tabela user_roles."
          )}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <Shield className="h-6 w-6 text-primary" />
            {t("Admin · Manage matches", "Admin · Gerenciar partidas")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "Set kickoff, timezone, status, scores and the first scorer for each match.",
              "Defina o horário, fuso, status, placar e o primeiro a marcar de cada partida."
            )}
          </p>
        </div>
        <div className="rounded-lg border bg-card px-3 py-2 text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("Settled", "Definidos")}
          </div>
          <div className="text-lg font-black text-primary">
            {finishedCount}
            <span className="text-sm font-medium text-muted-foreground">
              {" "}
              / {matches.length}
            </span>
          </div>
        </div>
      </div>

      <div className="mb-5 rounded-xl border border-premium/40 bg-premium/5 p-4">
        <div className="mb-2 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-premium" />
          <h2 className="text-sm font-bold uppercase tracking-wider">
            {t("Preview badge celebration", "Pré-visualizar conquista de badge")}
          </h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => previewBadges(ALL_BADGES.map((b) => b.id))}
            className="inline-flex items-center gap-1.5 rounded-lg bg-premium px-3 py-2 text-sm font-bold text-premium-foreground hover:opacity-90"
          >
            <Sparkles className="h-4 w-4" />
            {t("Show all badges", "Mostrar todas")}
          </button>
          {ALL_BADGES.map((b) => {
            const lb = localizeBadge(b, lang);
            return (
              <button
                key={b.id}
                onClick={() => previewBadges([b.id])}
                className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2 text-sm font-medium hover:bg-accent"
                title={lb.name}
              >
                <BadgeIcon id={b.id} size="xs" />
                {lb.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-5 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider">
          <RefreshCw className="h-4 w-4 text-destructive" />
          {t("Danger zone", "Zonă periculoasă")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(
            "Irreversible actions. Use with caution.",
            "Acțiuni ireversibile. Folosește cu atenție."
          )}
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                disabled={resettingPreds}
                className="inline-flex items-center gap-1.5 rounded-lg bg-destructive px-3 py-2 text-sm font-bold text-destructive-foreground hover:opacity-90 disabled:opacity-50"
              >
                <RefreshCw className="h-4 w-4" />
                {t("Reset all predictions", "Resetează toate predicțiile")}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("Reset all predictions?", "Resetează toate predicțiile?")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "This deletes every prediction from every user, resets all match scores to 0:0 and statuses to upcoming, and restores original kickoff times. Player points are recomputed from these, so they reset to 0.",
                    "Ești sigur? Această acțiune este ireversibilă. Va șterge toate predicțiile, scorurile și statusurile meciurilor și va restaura programul inițial."
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Cancel", "Anulează")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetAllPredictions}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t("Confirm reset", "Confirmă")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <button
            type="button"
            disabled={recalcBadges}
            onClick={handleRecalculateBadges}
            className="inline-flex items-center gap-1.5 rounded-lg border-2 border-premium bg-background px-3 py-2 text-sm font-bold text-premium hover:bg-premium/10 disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            {t("Recalculate all badges", "Recalcular insígnias", "Recalculează toate insignele")}
          </button>

          <button
            type="button"
            disabled={verifyingTriggers}
            onClick={handleVerifyTriggers}
            className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2 text-xs font-bold hover:bg-accent disabled:opacity-50"
          >
            <ListChecks className="h-3.5 w-3.5" />
            {t("Verify triggers", undefined, "Verifică trigger-ele")}
          </button>

          <button
            type="button"
            disabled={checkingStatuses}
            onClick={handleCheckMatchStatuses}
            className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2 text-xs font-bold hover:bg-accent disabled:opacity-50"
          >
            <Clock className="h-3.5 w-3.5" />
            {t("Check match statuses", undefined, "Verifică statusuri meciuri")}
          </button>



          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                disabled={resettingLeagues}
                className="inline-flex items-center gap-1.5 rounded-lg border-2 border-destructive bg-background px-3 py-2 text-sm font-bold text-destructive hover:bg-destructive/10 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                {t("Delete all leagues", "Șterge toate ligile")}
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("Delete all leagues?", "Șterge toate ligile?")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "This removes every league, its members and leaderboards. User accounts and profiles are preserved. This action cannot be undone.",
                    "Ești sigur? Această acțiune este ireversibilă. Va șterge toate ligile, membrii și clasamentele. Conturile și profilurile rămân intacte."
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("Cancel", "Anulează")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAllLeagues}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  <Users className="mr-1 h-4 w-4" />
                  {t("Confirm delete", "Confirmă")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {triggerChecks && (
          <div className="mt-3 overflow-hidden rounded-lg border bg-background/60">
            <div className="grid gap-px bg-border text-xs sm:grid-cols-[1.1fr_0.8fr_0.7fr_2fr]">
              {triggerChecks.map((row) => {
                const ok = row.present && row.enabled === "enabled";
                return (
                  <div key={row.trigger_name} className="contents">
                    <div className="bg-card px-3 py-2 font-mono text-[11px] font-semibold">{row.trigger_name}</div>
                    <div className="bg-card px-3 py-2 text-muted-foreground">{row.table_name}</div>
                    <div className={`bg-card px-3 py-2 font-bold ${ok ? "text-success" : "text-destructive"}`}>
                      {ok ? t("Active", undefined, "Activ") : row.present ? t("Disabled", undefined, "Dezactivat") : t("Missing", undefined, "Lipsește")}
                    </div>
                    <div className="bg-card px-3 py-2 text-muted-foreground">{row.purpose}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">
          {t("Loading matches…", "Carregando partidas…")}
        </div>
      ) : (
        (() => {
          const upcoming = resolvedMatches.filter((m) => m.status !== "finished");
          const settled = resolvedMatches.filter((m) => m.status === "finished");
          return (
            <Tabs defaultValue="upcoming" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="upcoming">
                  {t("Upcoming", undefined, "Următoarele")} ({upcoming.length})
                </TabsTrigger>
                <TabsTrigger value="settled">
                  {t("Settled", undefined, "Decise")} ({settled.length})
                </TabsTrigger>
                <TabsTrigger value="audit">
                  {t("Audit Log", undefined, "Jurnal")}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upcoming" className="mt-4">
                {upcoming.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    {t("No upcoming matches.", undefined, "Niciun meci viitor.")}
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {upcoming.map((m) => (
                      <AdminMatchRow
                        key={m.id}
                        match={m}
                        teams={teams}
                        homeTeamName={teamName(m.home_team)}
                        awayTeamName={teamName(m.away_team)}
                        homePlayers={m.home_team ? (playersIdx.byTeamCode[m.home_team] ?? []) : []}
                        awayPlayers={m.away_team ? (playersIdx.byTeamCode[m.away_team] ?? []) : []}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="settled" className="mt-4">
                {settled.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    {t("No settled matches yet.", undefined, "Niciun meci decis încă.")}
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {settled.map((m) => (
                      <AdminMatchRow
                        key={m.id}
                        match={m}
                        teams={teams}
                        homeTeamName={teamName(m.home_team)}
                        awayTeamName={teamName(m.away_team)}
                        homePlayers={m.home_team ? (playersIdx.byTeamCode[m.home_team] ?? []) : []}
                        awayPlayers={m.away_team ? (playersIdx.byTeamCode[m.away_team] ?? []) : []}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="audit" className="mt-4">
                <AuditLogPanel />
              </TabsContent>
            </Tabs>
          );
        })()
      )}
    </div>
  );
}

function isoToLocalInput(iso: string, tz: string): string {
  // Convert UTC ISO -> "YYYY-MM-DDTHH:mm" formatted as the wall-clock in tz.
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

function localInputToIso(local: string, tz: string): string {
  // Treat "YYYY-MM-DDTHH:mm" as wall-clock in tz, return UTC ISO.
  // Compute the offset of `tz` at that approximate instant.
  const [datePart, timePart] = local.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  // First approximation: build UTC
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi);
  // Determine what wall-clock that instant is in tz, then adjust.
  const tzWall = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(utcGuess));
  const get = (type: string) => Number(tzWall.find((p) => p.type === type)?.value ?? 0);
  const wallUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour"),
    get("minute")
  );
  const offset = utcGuess - wallUtc; // how far tz is from UTC at that moment
  return new Date(utcGuess + offset).toISOString();
}

function AdminMatchRow({
  match,
  teams,
  homeTeamName,
  awayTeamName,
  homePlayers,
  awayPlayers,
}: {
  match: MatchRow;
  teams: TeamRow[];
  homeTeamName: string;
  awayTeamName: string;
  homePlayers: PlayerRow[];
  awayPlayers: PlayerRow[];
}) {
  const t = useT();
  const tc = useTCountry();
  const updateMatch = useUpdateMatch();
  const qc = useQueryClient();

  const [tz, setTz] = useState(match.timezone);
  const [kickoffLocal, setKickoffLocal] = useState(() =>
    isoToLocalInput(match.kickoff_at, match.timezone)
  );
  const [homeTeamCode, setHomeTeamCode] = useState<string | null>(match.home_team);
  const [awayTeamCode, setAwayTeamCode] = useState<string | null>(match.away_team);

  useEffect(() => { setHomeTeamCode(match.home_team); }, [match.home_team]);
  useEffect(() => { setAwayTeamCode(match.away_team); }, [match.away_team]);
  const [home, setHome] = useState<number>(match.home_score ?? 0);
  const [away, setAway] = useState<number>(match.away_score ?? 0);
  const [scorerTeam, setScorerTeam] = useState<"" | ScorerSide>(
    match.first_scorer_team ?? ""
  );
  const [scorerPlayerId, setScorerPlayerId] = useState<string>(
    match.first_scorer_player_id ?? ""
  );
  const [penaltiesEnabled, setPenaltiesEnabled] = useState(
    match.home_pen_score != null || match.away_pen_score != null
  );
  const [homePen, setHomePen] = useState<number>(match.home_pen_score ?? 0);
  const [awayPen, setAwayPen] = useState<number>(match.away_pen_score ?? 0);

  const isFinished = match.status === "finished";
  const isLive = match.status === "live";
  const isKnockout = match.stage !== "group";
  const teamsMissing = !homeTeamCode || !awayTeamCode;
  const homeLabel = homeTeamCode ? tc(homeTeamName) : (match.home_slot_label ?? "TBD");
  const awayLabel = awayTeamCode ? tc(awayTeamName) : (match.away_slot_label ?? "TBD");

  const playerList = scorerTeam ? [...homePlayers, ...awayPlayers] : [];

  function save(
    extra: Partial<{ status: MatchRow["status"]; home_team: string | null; away_team: string | null; home_score: number | null; away_score: number | null; home_pen_score: number | null; away_pen_score: number | null; first_scorer_team: ScorerSide | null; first_scorer_player_id: string | null; predictions_locked: boolean }> = {},
    afterSave?: () => void
  ) {
    const nextScorerTeam = extra.first_scorer_team;
    const nextScorerPlayerId = extra.first_scorer_player_id;
    const normalizedExtra = {
      ...extra,
      ...("first_scorer_player_id" in extra
        ? { first_scorer_player_id: nextScorerTeam ? nextScorerPlayerId || null : null }
        : {}),
    };
    updateMatch.mutate(
      {
        id: match.id,
        kickoff_at: localInputToIso(kickoffLocal, tz),
        timezone: tz,
        home_team: homeTeamCode,
        away_team: awayTeamCode,
        ...normalizedExtra,
      },
      {
        onSuccess: () => {
          toast.success(t("Saved", "Salvo"), {
            description: `${homeLabel} vs ${awayLabel}`,
          });
          afterSave?.();
        },
        onError: (err: unknown) => {
          // eslint-disable-next-line no-console
          console.error("[admin] match update failed", err);
          toast.error(t("Save failed", "Falha ao salvar"), { description: errorMessage(err) });
        },
      }
    );
  }

  async function propagateWinner(homeScore: number, awayScore: number, homePenScore: number, awayPenScore: number, usePen: boolean) {
    let winnerCode: string | null = null;
    let loserCode: string | null = null;
    if (homeScore > awayScore) { winnerCode = homeTeamCode; loserCode = awayTeamCode; }
    else if (awayScore > homeScore) { winnerCode = awayTeamCode; loserCode = homeTeamCode; }
    else if (usePen) {
      if (homePenScore > awayPenScore) { winnerCode = homeTeamCode; loserCode = awayTeamCode; }
      else if (awayPenScore > homePenScore) { winnerCode = awayTeamCode; loserCode = homeTeamCode; }
    }
    if (!winnerCode) return;

    const updates: Array<{ id: number; patch: { home_team?: string; away_team?: string } }> = [];
    for (const m of MATCHES) {
      const patch: { home_team?: string; away_team?: string } = {};
      if (m.homeSlot === `W${match.id}` && winnerCode) patch.home_team = winnerCode;
      if (m.awaySlot === `W${match.id}` && winnerCode) patch.away_team = winnerCode;
      if (m.homeSlot === `L${match.id}` && loserCode) patch.home_team = loserCode;
      if (m.awaySlot === `L${match.id}` && loserCode) patch.away_team = loserCode;
      if (Object.keys(patch).length) updates.push({ id: m.id, patch });
    }
    if (!updates.length) return;

    await Promise.all(
      updates.map(({ id, patch }) =>
        supabase.from("matches" as never).update(patch as never).eq("id", id)
      )
    );
    await qc.invalidateQueries({ queryKey: ["matches"] });
  }

  function settleScore() {
    if (isKnockout && teamsMissing) {
      toast.error(
        t("Select both teams first", "Selecione os dois times primeiro"),
        { description: t("Knockout matches need both teams assigned before settling a result.", "Partidas eliminatórias exigem ambos os times definidos.") }
      );
      return;
    }
    const hScore = home;
    const aScore = away;
    const hPen = homePen;
    const aPen = awayPen;
    const usePen = penaltiesEnabled;
    save(
      {
        status: "finished",
        home_score: hScore,
        away_score: aScore,
        home_pen_score: usePen ? hPen : null,
        away_pen_score: usePen ? aPen : null,
        first_scorer_team: scorerTeam || null,
        first_scorer_player_id: scorerPlayerId || null,
      },
      () => propagateWinner(hScore, aScore, hPen, aPen, usePen)
    );
  }

  function setLive() {
    if (isKnockout && teamsMissing) {
      toast.error(
        t("Select both teams first", "Selecione os dois times primeiro"),
        { description: t("Knockout matches need both teams assigned before going live.", "Partidas eliminatórias exigem ambos os times definidos.") }
      );
      return;
    }
    save({ status: "live" });
  }

  function resetMatch() {
    setPenaltiesEnabled(false);
    setHomePen(0);
    setAwayPen(0);
    save({
      status: "upcoming",
      home_score: null,
      away_score: null,
      home_pen_score: null,
      away_pen_score: null,
      first_scorer_team: null,
      first_scorer_player_id: null,
    });
  }

  return (
    <div
      className={`rounded-xl border bg-card p-4 ${
        isFinished ? "border-success/50" : isLive ? "border-live/60" : ""
      }`}
    >
      <div className="mb-3 flex items-center justify-between gap-2 text-xs">
        <span className="rounded-full bg-muted px-2 py-0.5 font-semibold uppercase tracking-wider text-muted-foreground">
          {t("Group", "Grupo")} {match.group_letter ?? "—"}
        </span>
        {isFinished ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 font-semibold text-success">
            <Check className="h-3 w-3" />
            {t("Settled", "Definido")}
          </span>
        ) : isLive ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-live/15 px-2 py-0.5 font-semibold uppercase tracking-wider text-live">
            <span className="live-dot inline-block h-2 w-2 rounded-full bg-live" />
            {t("Live", "Ao vivo")}
          </span>
        ) : null}
      </div>

      {isKnockout && (
        <div className="mb-3 grid gap-2 sm:grid-cols-2">
          <TeamPicker
            label={`${t("Home", "Mandante")} (${match.home_slot_label ?? "?"})`}
            value={homeTeamCode}
            teams={teams}
            onChange={setHomeTeamCode}
          />
          <TeamPicker
            label={`${t("Away", "Visitante")} (${match.away_slot_label ?? "?"})`}
            value={awayTeamCode}
            teams={teams}
            onChange={setAwayTeamCode}
          />
        </div>
      )}

      {/* Teams + score */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <div className="text-right font-semibold">{homeLabel}</div>
        <div className="flex items-center gap-2">
          <ScoreInput value={home} onChange={setHome} />
          <span className="text-muted-foreground">–</span>
          <ScoreInput value={away} onChange={setAway} />
        </div>
        <div className="font-semibold">{awayLabel}</div>
      </div>

      {/* Kickoff + timezone */}
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr]">
        <label className="block min-w-0 overflow-hidden">
          <span className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Clock className="h-3 w-3" /> {t("Kickoff (local)", "Início (local)")}
          </span>
          <input
            type="datetime-local"
            value={kickoffLocal}
            onChange={(e) => setKickoffLocal(e.target.value)}
            className="w-full min-w-0 max-w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("Timezone", "Fuso")}
          </span>
          <select
            value={tz}
            onChange={(e) => {
              const next = e.target.value;
              // Keep the same wall-clock semantics: re-interpret current display
              // value as new tz wall-clock by first converting back to UTC then
              // to the new tz string.
              const isoNow = localInputToIso(kickoffLocal, tz);
              setTz(next);
              setKickoffLocal(isoToLocalInput(isoNow, next));
            }}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            {TIMEZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* First scorer */}
      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_1fr]">
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("First to score", "Primeiro time a marcar")}
          </span>
          <select
            value={scorerTeam}
            onChange={(e) => {
              setScorerTeam(e.target.value as "" | ScorerSide);
              setScorerPlayerId("");
            }}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t("— None —", "— Nenhum —")}</option>
            <option value="H">{homeLabel}</option>
            <option value="A">{awayLabel}</option>
          </select>
        </label>
        <div>
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("First scorer (player)", "Primeiro a marcar (jogador)")}
          </span>
          <ScorerPicker
            players={playerList}
            value={scorerPlayerId}
            onChange={setScorerPlayerId}
            disabled={!scorerTeam}
          />
        </div>
      </div>

      {/* Penalties (KO matches only) */}
      {isKnockout && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("Penalties?", "Penalty-uri?")}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { setPenaltiesEnabled(false); setHomePen(0); setAwayPen(0); }}
              className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${
                !penaltiesEnabled
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-accent"
              }`}
            >
              {t("No", "Nu")}
            </button>
            <button
              type="button"
              onClick={() => setPenaltiesEnabled(true)}
              className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${
                penaltiesEnabled
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-accent"
              }`}
            >
              {t("Yes", "Da")}
            </button>
            {penaltiesEnabled && (
              <div className="flex items-center gap-2 ml-2">
                <ScoreInput value={homePen} onChange={setHomePen} />
                <span className="text-muted-foreground text-sm">–</span>
                <ScoreInput value={awayPen} onChange={setAwayPen} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {t("pen.", "pen.")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          onClick={() => save()}
          disabled={updateMatch.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          <Save className="h-4 w-4" />
          {t("Save schedule", "Salvar horário")}
        </button>
        {!isFinished && !isLive && (
          <button
            onClick={setLive}
            disabled={updateMatch.isPending || (isKnockout && teamsMissing)}
            title={isKnockout && teamsMissing ? t("Select both teams first", "Selecione os dois times primeiro") : undefined}
            className="inline-flex items-center gap-1.5 rounded-lg border-2 border-live bg-live/10 px-3 py-2 text-sm font-bold text-live hover:bg-live/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Radio className="h-4 w-4" />
            {t("Set live", "Ao vivo")}
          </button>
        )}
        <button
          onClick={settleScore}
          disabled={updateMatch.isPending || (isKnockout && teamsMissing)}
          title={isKnockout && teamsMissing ? t("Select both teams first", "Selecione os dois times primeiro") : undefined}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Trophy className="h-4 w-4" />
          {isFinished ? t("Update result", "Atualizar resultado") : t("Settle result", "Definir resultado")}
        </button>
        {(isFinished || isLive) && (
          <button
            onClick={resetMatch}
            disabled={updateMatch.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg border bg-background px-3 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
            {t("Reset", "Resetar")}
          </button>
        )}
        {(() => {
          // predictions_locked may not exist on older MatchRow fetches; cast.
          const locked = (match as MatchRow & { predictions_locked?: boolean }).predictions_locked ?? false;
          return (
            <button
              type="button"
              onClick={() => save({ predictions_locked: !locked })}
              disabled={updateMatch.isPending}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${
                locked
                  ? "border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20"
                  : "bg-background hover:bg-accent"
              }`}
            >
              {locked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              {locked
                ? t("Unlock predictions", "Deblochează pronosticuri")
                : t("Lock predictions", "Blochează pronosticuri")}
            </button>
          );
        })()}
      </div>
      {isKnockout && teamsMissing && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t(
            "Assign both teams before settling or going live. Predictions stay locked until both are set.",
            "Defina os dois times antes de marcar como ao vivo ou definir o resultado."
          )}
        </p>
      )}
    </div>
  );
}

function TeamPicker({
  label,
  value,
  teams,
  onChange,
}: {
  label: string;
  value: string | null;
  teams: TeamRow[];
  onChange: (code: string | null) => void;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
      >
        <option value="">— TBD —</option>
        {teams.map((tt) => (
          <option key={tt.code} value={tt.code}>
            {tt.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function ScorerPicker({
  players,
  value,
  onChange,
  disabled,
}: {
  players: PlayerRow[];
  value: string;
  onChange: (id: string) => void;
  disabled: boolean;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = players.find((p) => p.id === value);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? players.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.team_code.toLowerCase().includes(q)
      )
    : players;

  const groups = groupByPosition(filtered);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
      >
        <span className={selected ? "text-foreground" : "text-muted-foreground"}>
          {selected ? `${selected.name} [${selected.team_code}]` : "— None —"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border bg-card shadow-xl">
          <div className="p-2">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search player or team…"
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-base outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          <div className="max-h-64 overflow-y-auto pb-1 show-scrollbar">
            <button
              type="button"
              onClick={() => { onChange(""); setSearch(""); setOpen(false); }}
              className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
            >
              — None —
            </button>
            {groups.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                No players found
              </div>
            ) : (
              groups.map(({ pos, players: group }) => (
                <div key={pos}>
                  <div className="bg-muted/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                    {pos}
                  </div>
                  {group.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { onChange(p.id); setSearch(""); setOpen(false); }}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent ${
                        value === p.id ? "bg-primary/10 font-semibold" : ""
                      }`}
                    >
                      <span>{p.name}</span>
                      <span className="ml-2 shrink-0 text-xs font-semibold text-muted-foreground">
                        [{p.team_code}]
                      </span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const POSITION_ORDER = ["FWD", "MID", "DEF", "GK"];

function groupByPosition(players: PlayerRow[]) {
  const groups: Record<string, PlayerRow[]> = {};
  for (const p of players) {
    const pos = (p.position ?? "—").toUpperCase();
    (groups[pos] ??= []).push(p);
  }
  for (const pos of Object.keys(groups)) {
    groups[pos].sort((a, b) => a.name.localeCompare(b.name));
  }
  const ordered = POSITION_ORDER.filter((pos) => groups[pos]?.length);
  const others = Object.keys(groups).filter((pos) => !POSITION_ORDER.includes(pos)).sort();
  return [...ordered, ...others].map((pos) => ({ pos, players: groups[pos] }));
}

function ScoreInput({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-11 w-14 rounded-lg border border-input bg-background text-center text-lg font-black"
    >
      {Array.from({ length: 11 }).map((_, i) => (
        <option key={i} value={i}>
          {i}
        </option>
      ))}
    </select>
  );
}
